import type { ExecutionMode } from '@/components/worktree-controls';

/**
 * How a submission made while the agent is busy should be delivered: `queue`
 * appends it to the follow-up queue (the default, matching Cursor — the queued
 * list with per-item Send Now/edit/delete controls is shown above the
 * composer), `steer` injects it into the running turn. The composer flips into
 * the opposite of the default (queue) when the user holds the steer modifier,
 * tracked by `oppositeFollowUpModeRef`.
 */
export type FollowUpMode = 'steer' | 'queue';

/** Resolve the follow-up delivery mode from the "opposite mode" latch. */
export function resolveFollowUpMode(oppositeFollowUpMode: boolean): FollowUpMode {
  return oppositeFollowUpMode ? 'steer' : 'queue';
}

/**
 * Whether a composer submit should be dropped without any side effects: while
 * previewing, when the composer cannot submit (and no turn is running to steer),
 * or when the message carries no text, files, browser selections, or response
 * annotations.
 */
export function composerSubmitIgnored(input: {
  preview: boolean;
  busy: boolean;
  canSubmit: boolean;
  hasText: boolean;
  fileCount: number;
  elementSelectionCount: number;
  annotationCount: number;
}): boolean {
  if (input.preview) return true;
  if (!input.busy && !input.canSubmit) return true;
  return (
    !input.hasText &&
    input.fileCount === 0 &&
    input.elementSelectionCount === 0 &&
    input.annotationCount === 0
  );
}

/**
 * The terminal routing decision for a non-ignored composer submit:
 * - `steer` / `queue`: the agent is busy, deliver as a follow-up.
 * - `edit`: replace the transcript from `editedIndex` and resend.
 * - `provision-worktree`: first message of a fresh managed-worktree conversation.
 * - `submit`: an ordinary send.
 */
export type ComposerSubmitAction =
  | { type: 'steer' }
  | { type: 'queue' }
  | { type: 'edit'; editedIndex: number }
  | { type: 'provision-worktree' }
  | { type: 'submit' };

/**
 * Decide how a non-ignored submit is routed. Busy turns take the follow-up
 * path (steer/queue) and never touch editing/worktree state. Otherwise an
 * in-progress edit that still resolves to a message wins, then the very first
 * message of a managed-worktree conversation triggers provisioning, and
 * everything else is an ordinary submit.
 */
export function composerSubmitAction(input: {
  busy: boolean;
  followUpMode: FollowUpMode;
  editingMessageId: string | null;
  editedIndex: number;
  messagesLength: number;
  executionMode: ExecutionMode;
  worktreeSourceRoot: string | null;
}): ComposerSubmitAction {
  if (input.busy) {
    return input.followUpMode === 'steer' ? { type: 'steer' } : { type: 'queue' };
  }
  if (input.editingMessageId !== null && input.editedIndex >= 0) {
    return { type: 'edit', editedIndex: input.editedIndex };
  }
  if (
    input.messagesLength === 0 &&
    input.executionMode === 'worktree' &&
    input.worktreeSourceRoot === null
  ) {
    return { type: 'provision-worktree' };
  }
  return { type: 'submit' };
}

/**
 * The action a single key press in the composer resolves to. `none` means the
 * key was not consumed (default typing/behaviour proceeds); everything else is
 * a handled action that the composer prevents default on. The composer's picker
 * (+, / and @) takes its own keys first; what it leaves comes here.
 */
export type ComposerKeyAction =
  | { type: 'none' }
  | { type: 'cancel-edit' }
  | { type: 'submit-opposite-follow-up' }
  | { type: 'submit' };

/**
 * Resolve what a composer key press does, in priority order:
 * 1. Escape cancels an in-progress edit when the picker is not showing.
 * 2. Cmd/Ctrl+Shift+Enter (while busy, the picker not showing) submits in the
 *    opposite follow-up mode.
 * 3. Bare Enter submits, except from a touch screen's keyboard, where it breaks
 *    the line (the send button sends; Cmd/Ctrl+Enter still does).
 * Unhandled keys fall through to `none`.
 */
export function resolveComposerKeyAction(input: {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  editing: boolean;
  busy: boolean;
  /** The picker shows (a / or @ being typed). */
  pickerOpen: boolean;
  /** Typed on a touch screen's own keyboard (a phone, a tablet without one attached). */
  touchKeyboard?: boolean;
}): ComposerKeyAction {
  const {
    key,
    shiftKey,
    metaKey,
    ctrlKey,
    editing,
    busy,
    pickerOpen,
    touchKeyboard = false,
  } = input;
  if (editing && key === 'Escape' && !pickerOpen) return { type: 'cancel-edit' };
  if (busy && key === 'Enter' && shiftKey && (metaKey || ctrlKey) && !pickerOpen)
    return { type: 'submit-opposite-follow-up' };
  if (key === 'Enter' && !shiftKey && (!touchKeyboard || metaKey || ctrlKey))
    return { type: 'submit' };
  return { type: 'none' };
}
